import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit, FiTrash2, FiShield, FiLock, FiUnlock } from 'react-icons/fi';
import { usersAPI, settingsAPI } from '../../services/api';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { UnifiedFilterHeaderCell, UnifiedTableToolbar } from '../../components/Common/UnifiedTableFilters';
import { runTableFilters } from '../../utils/tableFilters';

const MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'b2b_agencies', label: 'B2B Agency' },
  { key: 'students', label: 'Students' },
  { key: 'prospects', label: 'Prospects' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'mock_interviews', label: 'Mock Interviews' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'activities', label: 'Activities' },
  { key: 'payments', label: 'Payments' },
  { key: 'refund_requests', label: 'Refund Requests' },
  { key: 'agreements', label: 'Agreements' },
  { key: 'mailbox', label: 'Mailbox' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'companies', label: 'Companies' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'student_company_status', label: 'Company Status' },
  { key: 'mail_auto_config', label: 'Mail Auto Config' },
  { key: 'chat', label: 'Chat' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'users', label: 'Users' },
  { key: 'settings', label: 'Settings' },
];
const FRANCE_ONLY_MODULES = new Set(['companies', 'jobs', 'student_company_status', 'mail_auto_config']);
const PERMISSION_DRIVEN_ROLES = new Set(['admin', 'manager', 'sales_executive', 'company_relation']);

function userHasFranceModuleAccess(accessCountry) {
  const code = String(accessCountry || '').toUpperCase();
  return code === 'FRANCE' || code === 'BOTH';
}

const ACTIONS = ['can_read', 'can_create', 'can_update', 'can_delete'];
const ACTION_LABELS = { can_read: 'Read', can_create: 'Create', can_update: 'Update', can_delete: 'Delete' };

const UserList = () => {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const emptyForm = {
    name: '', email: '', phone: '', password: '',
    role: 'sales_executive', status: 'active',
    zoom_meeting_email: '', access_country: 'INDIA'
  };
  const [form, setForm] = useState(emptyForm);
  const [permissions, setPermissions] = useState([]);
  const [campusAccess, setCampusAccess] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  const hasFranceModuleAccess = userHasFranceModuleAccess(form.access_country);
  const visiblePermissionModules = MODULES.filter((m) => {
    if (!FRANCE_ONLY_MODULES.has(m.key)) return true;
    return hasFranceModuleAccess;
  });

  const initPermissions = useCallback((existingPerms) => {
    return MODULES.map(m => {
      const existing = (existingPerms || []).find(p => p.module === m.key);
      return {
        module: m.key,
        can_read: existing ? !!existing.can_read : false,
        can_create: existing ? !!existing.can_create : false,
        can_update: existing ? !!existing.can_update : false,
        can_delete: existing ? !!existing.can_delete : false,
      };
    });
  }, []);

  const getDefaultSalesExecutivePermissions = useCallback(() => {
    const template = {
      dashboard: { can_read: true, can_create: true, can_update: true, can_delete: true },
      b2b_agencies: { can_read: true, can_create: true, can_update: true, can_delete: true },
      students: { can_read: true, can_create: true, can_update: false, can_delete: false },
      prospects: { can_read: true, can_create: true, can_update: true, can_delete: false },
      interviews: { can_read: true, can_create: true, can_update: false, can_delete: false },
      mock_interviews: { can_read: true, can_create: true, can_update: false, can_delete: false },
      meetings: { can_read: true, can_create: true, can_update: false, can_delete: false },
      activities: { can_read: true, can_create: true, can_update: true, can_delete: true },
      payments: { can_read: true, can_create: false, can_update: false, can_delete: false },
      agreements: { can_read: true, can_create: true, can_update: true, can_delete: true },
      mailbox: { can_read: true, can_create: true, can_update: true, can_delete: true },
      tickets: { can_read: true, can_create: true, can_update: true, can_delete: false },
      companies: { can_read: true, can_create: false, can_update: false, can_delete: false },
      jobs: { can_read: true, can_create: false, can_update: false, can_delete: false },
      student_company_status: { can_read: false, can_create: false, can_update: false, can_delete: false },
      mail_auto_config: { can_read: false, can_create: false, can_update: false, can_delete: false },
      chat: { can_read: true, can_create: true, can_update: true, can_delete: false },
      notifications: { can_read: true, can_create: false, can_update: false, can_delete: false },
    };

    return MODULES.map((m) => ({
      module: m.key,
      can_read: !!template[m.key]?.can_read,
      can_create: !!template[m.key]?.can_create,
      can_update: !!template[m.key]?.can_update,
      can_delete: !!template[m.key]?.can_delete,
    }));
  }, []);

  const getDefaultPermissionsForRole = useCallback((roleValue) => {
    const normalizedRole = String(roleValue || '').toLowerCase();
    if (normalizedRole === 'admin') {
      return MODULES.map((m) => ({
        module: m.key,
        can_read: true,
        can_create: true,
        can_update: true,
        can_delete: true
      }));
    }
    if (normalizedRole === 'manager') {
      return MODULES.map((m) => {
        const blocked = m.key === 'users' || m.key === 'settings';
        return {
          module: m.key,
          can_read: !blocked,
          can_create: !blocked,
          can_update: !blocked,
          can_delete: !blocked
        };
      });
    }
    if (normalizedRole === 'company_relation') {
      return getDefaultSalesExecutivePermissions().map((p) => (
        p.module === 'student_company_status'
          ? { ...p, can_read: true, can_update: true }
          : p
      ));
    }
    return getDefaultSalesExecutivePermissions();
  }, [getDefaultSalesExecutivePermissions]);

  const initCampusAccess = useCallback((existingAccess, allCampuses) => {
    return (allCampuses || []).map(c => {
      const existing = (existingAccess || []).find(ca => ca.campus_id === c.campus_id);
      return {
        campus_id: c.campus_id,
        campus_name: c.campus_name,
        interview_access: existing ? !!existing.interview_access : false,
        mock_interview_access: existing ? !!existing.mock_interview_access : false,
      };
    });
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersAPI.getAll({});
      setUsers(res.data || []);
    } catch (err) { toast.error(t('users.failedLoad')); }
    setLoading(false);
  }, [t]);
  const userColumnFilters = useMemo(() => {
    const roles = Array.from(new Set(users.map((u) => String(u.role || '')).filter(Boolean)));
    const statuses = Array.from(new Set(users.map((u) => String(u.status || '')).filter(Boolean)));
    const countries = Array.from(new Set(users.map((u) => String(u.access_country || '')).filter(Boolean)));
    return [
      { key: 'name', label: t('users.tableUser'), field: 'name', type: 'text' },
      { key: 'email', label: t('users.tableEmail'), field: 'email', type: 'text' },
      { key: 'phone', label: t('users.tablePhone'), field: 'phone', type: 'text' },
      { key: 'role', label: t('users.tableRole'), field: 'role', type: 'multi', options: roles.map((r) => ({ value: r, label: t('users.roles.' + r) || formatRole(r) })) },
      { key: 'access_country', label: t('users.tableCountry'), field: 'access_country', type: 'multi', options: countries.map((c) => ({ value: c, label: c })) },
      { key: 'status', label: t('users.tableStatus'), field: 'status', type: 'multi', options: statuses.map((s) => ({ value: s, label: s })) },
      { key: 'created_at', label: t('users.tableJoined'), field: 'created_at', type: 'date' }
    ];
  }, [users, t]);

  const filteredUsers = useMemo(
    () =>
      runTableFilters(
        users,
        tableFilters,
        tableFilters,
        ['name', 'email', 'phone', 'role', 'status', 'access_country'],
        userColumnFilters
      ),
    [users, tableFilters, userColumnFilters]
  );
  const updateColumnFilters = useCallback(
    (updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })),
    []
  );


  useEffect(() => {
    loadUsers();
    settingsAPI.getCampuses().then(res => {
      setCampuses((res.data || []).filter(c => c.status === 'active'));
    }).catch(() => {});
  }, [loadUsers]);

  useEffect(() => {
    const userId = editingUser?.user_id;
    if (!showModal || !userId) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    settingsAPI
      .getAuditLog({ entity_type: 'user', entity_id: userId, limit: 200, page: 1 })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [editingUser?.user_id, showModal]);

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      toast.error(t('users.fillRequired')); return;
    }
    if (!editingUser && !form.password) {
      toast.error(t('users.passwordRequired')); return;
    }
    try {
      setSavingUser(true);
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      if (PERMISSION_DRIVEN_ROLES.has(String(form.role || '').toLowerCase())) {
        const normalizedPermissions = (permissions || []).map((p) => {
          if (!FRANCE_ONLY_MODULES.has(p.module)) return p;
          if (hasFranceModuleAccess) return p;
          return {
            ...p,
            can_read: false,
            can_create: false,
            can_update: false,
            can_delete: false
          };
        });
        payload.permissions = normalizedPermissions;
      }
      if (form.role === 'interviewer') {
        payload.campus_access = campusAccess.filter(ca => ca.interview_access || ca.mock_interview_access);
      }

      if (editingUser) {
        await usersAPI.update(editingUser.user_id, payload);
        toast.success(t('users.userUpdated'));
      } else {
        await usersAPI.create(payload);
        toast.success(t('users.userCreated'));
      }
      setShowModal(false);
      setEditingUser(null);
      setForm(emptyForm);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
    finally { setSavingUser(false); }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setForm({
      name: u.name || '', email: u.email,
      phone: u.phone || '', password: '', role: u.role,
      status: u.status || 'active',
      zoom_meeting_email: u.zoom_meeting_email || '',
      access_country: u.access_country || 'INDIA'
    });
    setPermissions(initPermissions(u.permissions));
    setCampusAccess(initCampusAccess(u.campus_access, campuses));
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setPermissions(getDefaultPermissionsForRole(emptyForm.role));
    setCampusAccess(initCampusAccess([], campuses));
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.user_id) { toast.error(t('users.cantDeleteSelf')); return; }
    if (!window.confirm(t('users.deleteConfirm'))) return;
    try {
      await usersAPI.delete(id);
      toast.success(t('users.userDeleted'));
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
  };

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      await usersAPI.update(u.user_id, { status: newStatus });
      toast.success(t('users.userStatusChanged', { status: newStatus }));
      loadUsers();
    } catch (err) { toast.error(t('common.failed')); }
  };

  const togglePermission = (moduleKey, action) => {
    setPermissions(prev => prev.map(p => {
      if (p.module !== moduleKey) return p;
      const updated = { ...p, [action]: !p[action] };
      if (action === 'can_read' && !updated.can_read) {
        updated.can_create = false;
        updated.can_update = false;
        updated.can_delete = false;
      }
      if (['can_create', 'can_update', 'can_delete'].includes(action) && updated[action]) {
        updated.can_read = true;
      }
      return updated;
    }));
  };

  const toggleAllModuleRead = (checked) => {
    const allowed = new Set(visiblePermissionModules.map((m) => m.key));
    setPermissions(prev => prev.map(p => {
      if (!allowed.has(p.module)) return p;
      return {
        ...p,
        can_read: checked,
        can_create: checked ? p.can_create : false,
        can_update: checked ? p.can_update : false,
        can_delete: checked ? p.can_delete : false
      };
    }));
  };

  const setAllPermissions = (enabled) => {
    const allowed = new Set(visiblePermissionModules.map((m) => m.key));
    setPermissions(prev => prev.map(p => {
      if (!allowed.has(p.module)) return p;
      return {
        ...p,
        can_read: enabled,
        can_create: enabled,
        can_update: enabled,
        can_delete: enabled
      };
    }));
  };

  const setModuleAllPermissions = (moduleKey, enabled) => {
    if (!visiblePermissionModules.some((m) => m.key === moduleKey)) return;
    setPermissions(prev => prev.map(p => {
      if (p.module !== moduleKey) return p;
      return {
        ...p,
        can_read: enabled,
        can_create: enabled,
        can_update: enabled,
        can_delete: enabled
      };
    }));
  };

  const toggleCampusAccess = (campusId, field) => {
    setCampusAccess(prev => prev.map(ca => {
      if (ca.campus_id !== campusId) return ca;
      return { ...ca, [field]: !ca[field] };
    }));
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': 'var(--purple)', 'admin': 'var(--primary)',
      'manager': '#0d9488',
      'sales_executive': 'var(--orange)', 'interviewer': 'var(--teal)',
      'company_relation': '#7c3aed'
    };
    return colors[role] || 'var(--text-muted)';
  };

  const formatRole = (role) => {
    return (role || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const allRoles = ['super_admin', 'admin', 'manager', 'sales_executive', 'interviewer', 'company_relation'];
  const roles = allRoles.filter(r => {
    if (r === 'company_relation') return String(form.access_country || '').toUpperCase() === 'FRANCE';
    return true;
  });
  const canManageUsers = ['super_admin', 'admin'].includes(String(currentUser?.role || '').toLowerCase());

  const handleRoleChange = (newRole) => {
    const prevRole = form.role;
    setForm(f => ({ ...f, role: newRole }));
    if (PERMISSION_DRIVEN_ROLES.has(String(newRole || '').toLowerCase())) {
      if (
        PERMISSION_DRIVEN_ROLES.has(String(prevRole || '').toLowerCase())
        && PERMISSION_DRIVEN_ROLES.has(String(editingUser?.role || '').toLowerCase())
      ) {
        setPermissions(initPermissions(editingUser.permissions));
      } else {
        setPermissions(getDefaultPermissionsForRole(newRole));
      }
    }
    if (newRole === 'interviewer') {
      setCampusAccess(initCampusAccess(editingUser?.role === 'interviewer' ? editingUser.campus_access : [], campuses));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiShield style={{ marginRight: 8 }} />{t('users.title')}</h2>
          <p className="page-subtitle">{t('users.usersCount', { count: users.length })}</p>
        </div>
        {canManageUsers && (
          <button className="btn btn-primary" onClick={handleAdd}>
            <FiPlus /> {t('users.addBtn')}
          </button>
        )}
      </div>

      <div className="filter-bar">
        <UnifiedTableToolbar
          draft={tableFilters}
          onDraftChange={setTableFilters}
          onReset={() => setTableFilters({ globalSearch: '', columns: {} })}
          hasActiveFilters={Boolean(tableFilters.globalSearch || Object.keys(tableFilters.columns || {}).length)}
          searchPlaceholder={t('users.searchPlaceholder')}
        />
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="table-container">
          <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <UnifiedFilterHeaderCell column={userColumnFilters[0]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[1]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[2]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[3]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[4]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[5]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                <UnifiedFilterHeaderCell column={userColumnFilters[6]} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                {canManageUsers && <th>{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.user_id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', background: getRoleBadgeColor(u.role),
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--white)',
                        fontSize: 14, fontWeight: 600, flexShrink: 0
                      }}>
                        {(u.name || '').split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div>
                        <strong>{u.name}</strong>
                        {u.user_id === currentUser?.user_id && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}>{t('users.you')}</span>}
                      </div>
                    </div>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.phone || '-'}</td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      color: 'var(--white)', background: getRoleBadgeColor(u.role)
                    }}>
                      {t('users.roles.' + u.role) || formatRole(u.role)}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                      {u.access_country === 'BOTH' ? t('users.countryBoth') : u.access_country === 'FRANCE' ? t('users.countryFrance') : t('users.countryIndia')}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      color: u.status === 'active' ? 'var(--success)' : 'var(--danger)',
                      background: u.status === 'active' ? 'var(--success-light)' : 'var(--danger-light)'
                    }}>
                      {u.status === 'active' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                  </td>
                  {canManageUsers && (
                    <td>
                      <div className="table-actions">
                        <button className="edit" title={t('common.edit')} onClick={() => handleEdit(u)}><FiEdit /></button>
                        <button className={u.status === 'active' ? 'view' : 'edit'} title={u.status === 'active' ? t('users.deactivate') : t('users.activate')} onClick={() => handleToggleStatus(u)}>
                          {u.status === 'active' ? <FiLock /> : <FiUnlock />}
                        </button>
                        {u.user_id !== currentUser?.user_id && (
                          <button className="delete" title={t('common.delete')} onClick={() => handleDelete(u.user_id)}><FiTrash2 /></button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filteredUsers.length === 0 && <tr><td colSpan={canManageUsers ? 8 : 7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>{t('users.noUsers')}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          if (savingUser) return;
          setShowModal(false);
          setHistory([]);
          setHistoryLoading(false);
        }}
        title={editingUser ? t('users.editTitle') : t('users.addTitle')}
        size="lg"
        footer={<><button className="btn btn-outline" disabled={savingUser} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" disabled={savingUser} onClick={handleSubmit}>{savingUser ? t('common.saving', 'Saving...') : (editingUser ? t('common.update') : t('common.create'))}</button></>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('users.fullName')} <span className="required">*</span></label>
            <input disabled={savingUser} value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('users.namePlaceholder')} />
          </div>
          <div className="form-group">
            <label>{t('users.emailLabel')} <span className="required">*</span></label>
            <input disabled={savingUser} type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('users.phoneLabel')}</label>
            <input disabled={savingUser} value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('users.zoomHostEmail')}</label>
            <input
              type="email"
              disabled={savingUser}
              value={form.zoom_meeting_email}
              onChange={(e) => setForm(f => ({ ...f, zoom_meeting_email: e.target.value }))}
              placeholder={t('users.zoomPlaceholder')}
            />
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
              {t('users.zoomHint')}
            </p>
          </div>
          <div className="form-group">
            <label>{editingUser ? t('users.newPassword') : t('users.password')} {!editingUser && <span className="required">*</span>}</label>
            <input disabled={savingUser} type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={editingUser ? t('users.passwordPlaceholderEdit') : t('users.passwordPlaceholder')} />
          </div>
          <div className="form-group">
            <label>{t('users.roleLabel')} <span className="required">*</span></label>
            <select disabled={savingUser} value={form.role} onChange={(e) => handleRoleChange(e.target.value)}>
              {roles.map(r => <option key={r} value={r}>{t('users.roles.' + r) || formatRole(r)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('users.statusLabel')}</label>
            <select disabled={savingUser} value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('users.countryAccess')}</label>
            <select disabled={savingUser} value={form.access_country} onChange={(e) => {
              const next = e.target.value;
              setForm(f => {
                const updates = { ...f, access_country: next };
                if (f.role === 'company_relation' && String(next).toUpperCase() !== 'FRANCE') {
                  updates.role = 'sales_executive';
                }
                return updates;
              });
            }}>
              <option value="INDIA">{t('users.countryIndia')}</option>
              <option value="FRANCE">{t('users.countryFrance')}</option>
              <option value="BOTH">{t('users.countryBoth')}</option>
            </select>
          </div>
        </div>

        {PERMISSION_DRIVEN_ROLES.has(String(form.role || '').toLowerCase()) && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{t('users.modulePermissions')}</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setAllPermissions(true)}
                  disabled={savingUser}
                >
                  {t('users.giveAll')}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setAllPermissions(false)}
                  disabled={savingUser}
                >
                  {t('users.removeAll')}
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>{t('users.moduleCol')}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>{t('users.allCol')}</th>
                    {ACTIONS.map(a => (
                      <th key={a} style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>
                        {t('users.actions.' + a.replace('can_', ''))}
                        {a === 'can_read' && (
                          <div style={{ marginTop: 2 }}>
                            <input
                              type="checkbox"
                              checked={
                                visiblePermissionModules.length > 0 &&
                                permissions
                                  .filter((p) => visiblePermissionModules.some((m) => m.key === p.module))
                                  .every((p) => p.can_read)
                              }
                              onChange={(e) => toggleAllModuleRead(e.target.checked)}
                              disabled={savingUser}
                              title={t('users.toggleAll')}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissions.filter((p) => visiblePermissionModules.some((m) => m.key === p.module)).map((p) => {
                    const moduleAllChecked = p.can_read && p.can_create && p.can_update && p.can_delete;
                    return (
                      <tr key={p.module} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                          {t(`users.modules.${p.module}`, {
                            defaultValue: MODULES.find((m) => m.key === p.module)?.label || p.module
                          })}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={moduleAllChecked}
                            onChange={(e) => setModuleAllPermissions(p.module, e.target.checked)}
                            disabled={savingUser}
                            style={{ cursor: 'pointer', width: 16, height: 16 }}
                          />
                        </td>
                        {ACTIONS.map(a => (
                          <td key={a} style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!!p[a]}
                              onChange={() => togglePermission(p.module, a)}
                              disabled={savingUser}
                              style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!hasFranceModuleAccess && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                Companies, Jobs, Company Status, and Mail Auto Config modules are available only for France or Both country access.
              </div>
            )}
          </div>
        )}

        {form.role === 'interviewer' && (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{t('users.campusAccess')}</h4>
            {campusAccess.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('users.noCampuses')}</p>
            ) : (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>{t('users.campusCol')}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>{t('users.interviewCol')}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>{t('users.mockInterviewCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {campusAccess.map(ca => (
                    <tr key={ca.campus_id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{ca.campus_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!ca.interview_access}
                          onChange={() => toggleCampusAccess(ca.campus_id, 'interview_access')}
                          disabled={savingUser}
                          style={{ cursor: 'pointer', width: 16, height: 16 }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!ca.mock_interview_access}
                          onChange={() => toggleCampusAccess(ca.campus_id, 'mock_interview_access')}
                          disabled={savingUser}
                          style={{ cursor: 'pointer', width: 16, height: 16 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {editingUser && (
          <div style={{ marginTop: 14 }}>
            <h4 style={{ margin: '8px 0 10px', fontSize: 14, fontWeight: 800 }}>{t('common.history')}</h4>
            {historyLoading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : (
              <AuditTimeline logs={history} showEntity={false} />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserList;
